import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { Item, VendorPayment } from '@/types';
import { formatCurrency, generateId, exportToCSV, exportToPDF, formatDate, printHtmlDocument } from '@/utils/calculations';
import StatusBadge from '@/components/base/StatusBadge';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
const emptyItemForm = (): Partial<Item> => ({
  name: '', sku: '', category: '', purchasePrice: 0,
  quantityPurchased: 0, currentStock: 0, lowStockAlert: 10,
  supplier: '', purchaseDate: new Date().toISOString().split('T')[0],
});

const emptyPaymentForm = () => ({
  date: new Date().toISOString().split('T')[0],
  vendorName: '',
  amount: 0,
  referenceNote: '',
  itemId: '',
});

type Tab = 'items' | 'payables' | 'vendors';

export default function InventoryPage() {
  const { isDark, items, addItem, updateItem, deleteItem, vendorPayments, addVendorPayment, deleteVendorPayment } = useApp();
  const allItems = items;

  const [activeTab, setActiveTab] = useState<Tab>('items');
  const [showItemModal, setShowItemModal] = useState(false);
  const [editItem, setEditItem] = useState<Item | null>(null);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [itemForm, setItemForm] = useState<Partial<Item>>(emptyItemForm());
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm());
  const [paymentSearch, setPaymentSearch] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);

  const categories = useMemo(() => [...new Set(allItems.map(i => i.category))], [allItems]);

  const filtered = useMemo(() => allItems.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase()) || i.sku.toLowerCase().includes(search.toLowerCase());
    const matchCat = !catFilter || i.category === catFilter;
    return matchSearch && matchCat;
  }), [allItems, search, catFilter]);

  const totalInventoryValue = useMemo(() => allItems.reduce((s, i) => s + i.purchasePrice * i.currentStock, 0), [allItems]);
  const totalOwedToVendors = useMemo(() => allItems.reduce((s, i) => s + i.totalPurchaseValue, 0), [allItems]);
  const totalPaidToVendors = useMemo(() => vendorPayments.reduce((s, p) => s + p.amount, 0), [vendorPayments]);
  const remainingOwed = totalOwedToVendors - totalPaidToVendors;

  const filteredPayments = useMemo(() => vendorPayments.filter(p =>
    p.vendorName.toLowerCase().includes(paymentSearch.toLowerCase()) ||
    p.referenceNote.toLowerCase().includes(paymentSearch.toLowerCase())
  ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()), [vendorPayments, paymentSearch]);

  // Vendor breakdown: group items by supplier, group payments by vendorName
  const vendorBreakdown = useMemo(() => {
    const map: Record<string, { vendorName: string; totalPurchased: number; items: Item[]; totalPaid: number; payments: VendorPayment[] }> = {};
    allItems.forEach(i => {
      const key = i.supplier || 'Unknown Vendor';
      if (!map[key]) map[key] = { vendorName: key, totalPurchased: 0, items: [], totalPaid: 0, payments: [] };
      map[key].totalPurchased += i.totalPurchaseValue;
      map[key].items.push(i);
    });
    vendorPayments.forEach(p => {
      const key = p.vendorName;
      if (!map[key]) map[key] = { vendorName: key, totalPurchased: 0, items: [], totalPaid: 0, payments: [] };
      map[key].totalPaid += p.amount;
      map[key].payments.push(p);
    });
    return Object.values(map).sort((a, b) => (b.totalPurchased - b.totalPaid) - (a.totalPurchased - a.totalPaid));
  }, [allItems, vendorPayments]);

  const selectedVendorData = useMemo(() => vendorBreakdown.find(v => v.vendorName === selectedVendor), [vendorBreakdown, selectedVendor]);

  const openAddItem = () => { setItemForm(emptyItemForm()); setEditItem(null); setShowItemModal(true); };
  const openEditItem = (i: Item) => { setItemForm({ ...i }); setEditItem(i); setShowItemModal(true); };

  const handleSaveItem = () => {
    if (!itemForm.name) return;
    const totalPurchaseValue = (itemForm.purchasePrice ?? 0) * (itemForm.quantityPurchased ?? 0);
    const item: Item = {
      id: editItem?.id ?? generateId(),
      name: itemForm.name ?? '',
      sku: itemForm.sku ?? '',
      category: itemForm.category ?? '',
      purchasePrice: Number(itemForm.purchasePrice ?? 0),
      quantityPurchased: Number(itemForm.quantityPurchased ?? 0),
      totalPurchaseValue,
      currentStock: Number(itemForm.currentStock ?? itemForm.quantityPurchased ?? 0),
      lowStockAlert: Number(itemForm.lowStockAlert ?? 10),
      supplier: itemForm.supplier ?? '',
      purchaseDate: itemForm.purchaseDate ?? '',
    };
    if (editItem) updateItem(item); else addItem(item);
    setShowItemModal(false);
  };

  const handleSavePayment = () => {
    if (!paymentForm.vendorName || !paymentForm.amount) return;
    const selectedItem = allItems.find(i => i.id === paymentForm.itemId);
    const payment: VendorPayment = {
      id: generateId(),
      date: paymentForm.date,
      vendorName: paymentForm.vendorName,
      amount: Number(paymentForm.amount),
      referenceNote: paymentForm.referenceNote,
      itemId: paymentForm.itemId || undefined,
      itemName: selectedItem?.name || undefined,
    };
    addVendorPayment(payment);
    setPaymentForm(emptyPaymentForm());
    setShowPaymentModal(false);
  };

  const handleExportVendorPDF = (vendorName: string) => {
    const vd = vendorBreakdown.find(v => v.vendorName === vendorName);
    if (!vd) return;
    const balance = vd.totalPurchased - vd.totalPaid;
    const itemRows = vd.items.map(i => [i.name, i.sku, String(i.quantityPurchased), formatCurrency(i.purchasePrice), formatCurrency(i.totalPurchaseValue)]);
    const paymentRows = vd.payments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(p => [formatDate(p.date), formatCurrency(p.amount), p.referenceNote || '—']);

    const html = `
      <!DOCTYPE html><html><head><title>Vendor Statement - ${vendorName}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 32px; color: #111; }
        h1 { font-size: 22px; margin-bottom: 4px; }
        .sub { color: #666; font-size: 13px; margin-bottom: 24px; }
        .summary { display: flex; gap: 24px; margin-bottom: 28px; }
        .summary-card { background: #f9f9f9; border: 1px solid #eee; border-radius: 8px; padding: 14px 20px; min-width: 140px; }
        .summary-card .label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.05em; }
        .summary-card .value { font-size: 20px; font-weight: 700; margin-top: 4px; }
        .red { color: #ef4444; } .green { color: #10b981; }
        h2 { font-size: 15px; margin: 20px 0 10px; border-bottom: 1px solid #eee; padding-bottom: 6px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { background: #f3f4f6; text-align: left; padding: 8px 10px; font-size: 11px; text-transform: uppercase; color: #555; }
        td { padding: 8px 10px; font-size: 13px; border-bottom: 1px solid #f0f0f0; }
        .footer { margin-top: 32px; font-size: 12px; color: #aaa; border-top: 1px solid #eee; padding-top: 12px; }
      </style></head><body>
      <h1>Vendor Statement</h1>
      <div class="sub">Vendor: <strong>${vendorName}</strong> &nbsp;·&nbsp; Generated: ${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      <div class="summary">
        <div class="summary-card"><div class="label">Total Purchased</div><div class="value">${formatCurrency(vd.totalPurchased)}</div></div>
        <div class="summary-card"><div class="label">Total Paid</div><div class="value green">${formatCurrency(vd.totalPaid)}</div></div>
        <div class="summary-card"><div class="label">Balance Owed</div><div class="value ${balance > 0 ? 'red' : 'green'}">${formatCurrency(balance)}</div></div>
      </div>
      <h2>Inventory Items Purchased</h2>
      <table><thead><tr><th>Item Name</th><th>SKU</th><th>Qty</th><th>Unit Price</th><th>Total Value</th></tr></thead>
      <tbody>${itemRows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}
      ${vd.items.length === 0 ? '<tr><td colspan="5" style="color:#aaa;text-align:center">No items linked</td></tr>' : ''}
      </tbody></table>
      <h2>Payment History</h2>
      <table><thead><tr><th>Date</th><th>Amount Paid</th><th>Reference / Note</th></tr></thead>
      <tbody>${paymentRows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}
      ${vd.payments.length === 0 ? '<tr><td colspan="3" style="color:#aaa;text-align:center">No payments recorded</td></tr>' : ''}
      </tbody></table>
      <div class="footer">BizFlow Business Dashboard &nbsp;·&nbsp; This is an auto-generated vendor statement.</div>
      </body></html>`;
    printHtmlDocument(html, `vendor-statement-${vendorName}`);
  };

  const td = `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const th = `text-xs font-semibold uppercase tracking-wide px-4 py-3 text-left whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-700'}`;
  const card = `rounded-xl border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`;

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Inventory & Vendor Payables</h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{allItems.length} items · Total stock value: {formatCurrency(totalInventoryValue)}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className={`${card} p-4`}>
          <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Owed to Vendors</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(totalOwedToVendors)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>From all inventory purchases</p>
        </div>
        <div className={`${card} p-4`}>
          <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Paid to Vendors</p>
          <p className="text-2xl font-bold text-emerald-500">{formatCurrency(totalPaidToVendors)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{vendorPayments.length} payment(s) recorded</p>
        </div>
        <div className={`rounded-xl border p-4 ${remainingOwed > 0 ? (isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200') : (isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100')}`}>
          <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${remainingOwed > 0 ? 'text-red-500' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>Balance Still Owed</p>
          <p className={`text-2xl font-bold ${remainingOwed > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{formatCurrency(remainingOwed)}</p>
          <p className={`text-xs mt-1 ${remainingOwed > 0 ? 'text-red-400' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>{remainingOwed > 0 ? 'Outstanding balance' : 'All paid up!'}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-full w-fit ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        {(['items', 'payables', 'vendors'] as Tab[]).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-5 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${activeTab === tab ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab === 'items' ? 'Inventory Items' : tab === 'payables' ? 'Vendor Payments' : 'Vendor Breakdown'}
          </button>
        ))}
      </div>

      {/* ── ITEMS TAB ── */}
      {activeTab === 'items' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex gap-3 flex-wrap flex-1">
              <input placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)} className={`px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder:text-gray-500' : 'bg-white border-gray-200 text-gray-700 placeholder:text-gray-400'}`} />
              <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className={`px-3 py-2 rounded-lg border text-sm outline-none cursor-pointer ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}>
                <option value="">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={() => exportToCSV(filtered.map(i => ({ Name: i.name, SKU: i.sku, Category: i.category, PurchasePrice: i.purchasePrice, Stock: i.currentStock, Value: i.purchasePrice * i.currentStock, Supplier: i.supplier })), 'inventory')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-download-line" /> Export CSV</button>
              <button onClick={openAddItem} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> Add Item</button>
            </div>
          </div>
          <div className={`${card} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                  <tr>
                    <th className={th}>Name</th><th className={th}>SKU</th><th className={th}>Category</th>
                    <th className={th}>Purchase Price</th><th className={th}>Qty Purchased</th>
                    <th className={th}>Total Purchase Value</th><th className={th}>Current Stock</th>
                    <th className={th}>Stock Value</th><th className={th}>Supplier</th>
                    <th className={th}>Status</th><th className={th}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filtered.map(i => (
                    <tr key={i.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                      <td className={`${td} font-medium`}>{i.name}</td>
                      <td className={`${td} font-mono text-xs`}>{i.sku}</td>
                      <td className={td}><span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{i.category}</span></td>
                      <td className={td}>{formatCurrency(i.purchasePrice)}</td>
                      <td className={td}>{i.quantityPurchased}</td>
                      <td className={`${td} font-semibold`}>{formatCurrency(i.totalPurchaseValue)}</td>
                      <td className={`${td} font-semibold ${i.currentStock <= i.lowStockAlert ? 'text-red-500' : 'text-emerald-500'}`}>{i.currentStock}</td>
                      <td className={`${td} font-semibold`}>{formatCurrency(i.purchasePrice * i.currentStock)}</td>
                      <td className={td}>{i.supplier}</td>
                      <td className={td}><StatusBadge status={i.currentStock <= i.lowStockAlert ? 'low' : 'ok'} /></td>
                      <td className={td}>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditItem(i)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"><i className="ri-edit-line text-sm" /></button>
                          <button onClick={() => setDeleteItemId(i.id)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><i className="ri-delete-bin-line text-sm" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={11} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No items found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── VENDOR PAYMENTS TAB ── */}
      {activeTab === 'payables' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <input placeholder="Search by vendor or note..." value={paymentSearch} onChange={e => setPaymentSearch(e.target.value)} className={`px-3 py-2 rounded-lg border text-sm outline-none flex-1 ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder:text-gray-500' : 'bg-white border-gray-200 text-gray-700 placeholder:text-gray-400'}`} />
            <div className="flex gap-2">
              <button onClick={() => exportToCSV(filteredPayments.map(p => ({ Date: p.date, Vendor: p.vendorName, Item: p.itemName || '-', Amount: p.amount, Note: p.referenceNote })), 'vendor-payments')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-download-line" /> Export CSV</button>
              <button onClick={() => { setPaymentForm(emptyPaymentForm()); setShowPaymentModal(true); }} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> Record Payment</button>
            </div>
          </div>
          {totalOwedToVendors > 0 && (
            <div className={`${card} p-4`}>
              <div className="flex justify-between items-center mb-2">
                <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Overall Payment Progress</span>
                <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{Math.min(100, Math.round((totalPaidToVendors / totalOwedToVendors) * 100))}% paid</span>
              </div>
              <div className={`w-full h-3 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${Math.min(100, (totalPaidToVendors / totalOwedToVendors) * 100)}%` }} />
              </div>
              <div className="flex justify-between mt-2">
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Paid: {formatCurrency(totalPaidToVendors)}</span>
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Remaining: {formatCurrency(Math.max(0, remainingOwed))}</span>
              </div>
            </div>
          )}
          <div className={`${card} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                  <tr><th className={th}>Date</th><th className={th}>Vendor Name</th><th className={th}>Related Item</th><th className={th}>Amount Paid</th><th className={th}>Reference / Note</th><th className={th}>Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredPayments.map(p => (
                    <tr key={p.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                      <td className={td}>{formatDate(p.date)}</td>
                      <td className={`${td} font-medium`}>{p.vendorName}</td>
                      <td className={td}>{p.itemName ? <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{p.itemName}</span> : <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>—</span>}</td>
                      <td className={`${td} font-semibold text-emerald-500`}>{formatCurrency(p.amount)}</td>
                      <td className={`${td} max-w-xs truncate`}>{p.referenceNote || '—'}</td>
                      <td className={td}><button onClick={() => setDeletePaymentId(p.id)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><i className="ri-delete-bin-line text-sm" /></button></td>
                    </tr>
                  ))}
                  {filteredPayments.length === 0 && <tr><td colSpan={6} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No payments recorded yet. Click &quot;Record Payment&quot; to add one.</td></tr>}
                </tbody>
                {filteredPayments.length > 0 && (
                  <tfoot className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                    <tr>
                      <td colSpan={3} className={`px-4 py-3 text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total Paid</td>
                      <td className="px-4 py-3 text-sm font-bold text-emerald-500">{formatCurrency(filteredPayments.reduce((s, p) => s + p.amount, 0))}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── VENDOR BREAKDOWN TAB ── */}
      {activeTab === 'vendors' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Vendor list */}
          <div className="lg:col-span-2 space-y-3">
            <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{vendorBreakdown.length} Vendor{vendorBreakdown.length !== 1 ? 's' : ''}</p>
            {vendorBreakdown.length === 0 && (
              <div className={`${card} p-6 text-center`}>
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No vendors yet. Add inventory items with supplier names to see them here.</p>
              </div>
            )}
            {vendorBreakdown.map(v => {
              const balance = v.totalPurchased - v.totalPaid;
              const pct = v.totalPurchased > 0 ? Math.min(100, (v.totalPaid / v.totalPurchased) * 100) : 100;
              const isSelected = selectedVendor === v.vendorName;
              return (
                <button key={v.vendorName} onClick={() => setSelectedVendor(isSelected ? null : v.vendorName)}
                  className={`w-full text-left rounded-xl border p-4 cursor-pointer transition-all ${isSelected ? 'border-emerald-500 ring-1 ring-emerald-500' : isDark ? 'bg-gray-900 border-gray-800 hover:border-gray-700' : 'bg-white border-gray-100 hover:border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{v.vendorName}</p>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{v.items.length} item{v.items.length !== 1 ? 's' : ''} · {v.payments.length} payment{v.payments.length !== 1 ? 's' : ''}</p>
                    </div>
                    <span className={`text-sm font-bold whitespace-nowrap ${balance > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{formatCurrency(balance)}</span>
                  </div>
                  <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Paid {Math.round(pct)}%</span>
                    <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>of {formatCurrency(v.totalPurchased)}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Vendor detail panel */}
          <div className="lg:col-span-3">
            {!selectedVendorData ? (
              <div className={`${card} p-10 flex flex-col items-center justify-center text-center h-full min-h-[300px]`}>
                <div className={`w-12 h-12 flex items-center justify-center rounded-full mb-3 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}><i className={`ri-store-2-line text-xl ${isDark ? 'text-gray-500' : 'text-gray-400'}`} /></div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select a vendor to view details</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Vendor header */}
                <div className={`${card} p-5`}>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedVendorData.vendorName}</h3>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Vendor Statement</p>
                    </div>
                    <button onClick={() => handleExportVendorPDF(selectedVendorData.vendorName)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">
                      <i className="ri-file-pdf-line" /> Export PDF
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Total Purchased', value: formatCurrency(selectedVendorData.totalPurchased), color: isDark ? 'text-white' : 'text-gray-900' },
                      { label: 'Total Paid', value: formatCurrency(selectedVendorData.totalPaid), color: 'text-emerald-500' },
                      { label: 'Balance Owed', value: formatCurrency(selectedVendorData.totalPurchased - selectedVendorData.totalPaid), color: (selectedVendorData.totalPurchased - selectedVendorData.totalPaid) > 0 ? 'text-red-500' : 'text-emerald-500' },
                    ].map(s => (
                      <div key={s.label} className={`rounded-lg p-3 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</p>
                        <p className={`text-base font-bold mt-0.5 ${s.color}`}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Items from this vendor */}
                <div className={`${card} overflow-hidden`}>
                  <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Items Purchased</p>
                  </div>
                  {selectedVendorData.items.length === 0 ? (
                    <p className={`text-sm text-center py-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No items linked to this vendor</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                          <tr><th className={th}>Item</th><th className={th}>SKU</th><th className={th}>Qty</th><th className={th}>Unit Price</th><th className={th}>Total Value</th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                          {selectedVendorData.items.map(i => (
                            <tr key={i.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                              <td className={`${td} font-medium`}>{i.name}</td>
                              <td className={`${td} font-mono text-xs`}>{i.sku}</td>
                              <td className={td}>{i.quantityPurchased}</td>
                              <td className={td}>{formatCurrency(i.purchasePrice)}</td>
                              <td className={`${td} font-semibold`}>{formatCurrency(i.totalPurchaseValue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Payments to this vendor */}
                <div className={`${card} overflow-hidden`}>
                  <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Payment History</p>
                    <button onClick={() => { setPaymentForm({ ...emptyPaymentForm(), vendorName: selectedVendorData.vendorName }); setShowPaymentModal(true); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">
                      <i className="ri-add-line" /> Add Payment
                    </button>
                  </div>
                  {selectedVendorData.payments.length === 0 ? (
                    <p className={`text-sm text-center py-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No payments recorded for this vendor</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                          <tr><th className={th}>Date</th><th className={th}>Amount</th><th className={th}>Reference</th><th className={th}>Actions</th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                          {[...selectedVendorData.payments].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(p => (
                            <tr key={p.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                              <td className={td}>{formatDate(p.date)}</td>
                              <td className={`${td} font-semibold text-emerald-500`}>{formatCurrency(p.amount)}</td>
                              <td className={`${td} max-w-xs truncate`}>{p.referenceNote || '—'}</td>
                              <td className={td}><button onClick={() => setDeletePaymentId(p.id)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><i className="ri-delete-bin-line text-sm" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                          <tr>
                            <td className={`px-4 py-3 text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total</td>
                            <td className="px-4 py-3 text-sm font-bold text-emerald-500">{formatCurrency(selectedVendorData.totalPaid)}</td>
                            <td colSpan={2} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      <Modal isOpen={showItemModal} onClose={() => setShowItemModal(false)} title={editItem ? 'Edit Item' : 'Add Inventory Item'} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Item Name *', field: 'name', type: 'text' },
            { label: 'SKU / Code', field: 'sku', type: 'text' },
            { label: 'Category', field: 'category', type: 'text' },
            { label: 'Supplier Name', field: 'supplier', type: 'text' },
            { label: 'Purchase Price per Item', field: 'purchasePrice', type: 'number' },
            { label: 'Qty Purchased', field: 'quantityPurchased', type: 'number' },
            { label: 'Current Stock', field: 'currentStock', type: 'number' },
            { label: 'Low Stock Alert', field: 'lowStockAlert', type: 'number' },
            { label: 'Purchase Date', field: 'purchaseDate', type: 'date' },
          ].map(f => (
            <div key={f.field}>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{f.label}</label>
              <input type={f.type} value={(itemForm as Record<string, unknown>)[f.field] as string ?? ''} onChange={e => setItemForm(p => ({ ...p, [f.field]: f.type === 'number' ? Number(e.target.value) : e.target.value }))} className={inputCls} />
            </div>
          ))}
          <div className={`sm:col-span-2 rounded-lg p-3 ${isDark ? 'bg-gray-800' : 'bg-emerald-50'}`}>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Purchase Value (owed to vendor)</p>
            <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency((itemForm.purchasePrice ?? 0) * (itemForm.quantityPurchased ?? 0))}</p>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>This amount will be added to your vendor payables balance</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-5">
          <button onClick={() => setShowItemModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
          <button onClick={handleSaveItem} className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">Save Item</button>
        </div>
      </Modal>

      {/* Record Vendor Payment Modal */}
      <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Record Vendor Payment" size="md">
        <div className="space-y-4">
          <div className={`rounded-lg p-3 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-100'}`}>
            <p className={`text-xs font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Current Balance Owed to Vendors</p>
            <p className={`text-xl font-bold ${remainingOwed > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{formatCurrency(remainingOwed)}</p>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Payment Date *</label>
            <input type="date" value={paymentForm.date} onChange={e => setPaymentForm(p => ({ ...p, date: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Vendor Name *</label>
            <input type="text" placeholder="e.g. ABC Supplies Ltd." value={paymentForm.vendorName} onChange={e => setPaymentForm(p => ({ ...p, vendorName: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Amount Paid *</label>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={paymentForm.amount || ''} onChange={e => setPaymentForm(p => ({ ...p, amount: Number(e.target.value) }))} className={inputCls} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Related Inventory Item (optional)</label>
            <select value={paymentForm.itemId} onChange={e => setPaymentForm(p => ({ ...p, itemId: e.target.value }))} className={`${inputCls} cursor-pointer`}>
              <option value="">— Not linked to a specific item —</option>
              {allItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.supplier})</option>)}
            </select>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Reference / Note</label>
            <input type="text" placeholder="e.g. Invoice #1234, partial payment..." value={paymentForm.referenceNote} onChange={e => setPaymentForm(p => ({ ...p, referenceNote: e.target.value }))} className={inputCls} />
          </div>
          {paymentForm.amount > 0 && (
            <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-800' : 'bg-emerald-50'}`}>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Balance after this payment</p>
              <p className={`text-lg font-bold ${remainingOwed - paymentForm.amount > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{formatCurrency(remainingOwed - paymentForm.amount)}</p>
            </div>
          )}
        </div>
        <div className="flex gap-3 justify-end mt-5">
          <button onClick={() => setShowPaymentModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
          <button onClick={handleSavePayment} disabled={!paymentForm.vendorName || !paymentForm.amount} className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">Record Payment</button>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteItemId} onClose={() => setDeleteItemId(null)} onConfirm={() => deleteItemId && deleteItem(deleteItemId)} title="Delete Item" message="Delete this inventory item? This will also affect your vendor payables total." />
      <ConfirmDialog isOpen={!!deletePaymentId} onClose={() => setDeletePaymentId(null)} onConfirm={() => { if (deletePaymentId) { deleteVendorPayment(deletePaymentId); } }} title="Delete Payment" message="Delete this vendor payment record? The amount will be added back to your balance owed." />
    </div>
  );
}
